import { Fragment, type ReactNode } from 'react'
import { OF } from './ofApi'

/**
 * OnlyFans text (posts, comments, bios) is HTML: paragraphs, line breaks,
 * bold/italic and @mention links. It is rebuilt as React elements from an
 * allow-list, so nothing from OnlyFans can run script or style the page.
 */
const INLINE: Record<string, string> = { STRONG: 'strong', B: 'strong', EM: 'em', I: 'em', U: 'u', S: 's', SPAN: 'span' }

function linkHref(raw: string | null): string | null {
  if (!raw) return null
  if (raw.startsWith('/')) return OF + raw
  try {
    const url = new URL(raw)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null
  } catch {
    return null
  }
}

function render(node: Node, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent
  if (node.nodeType !== Node.ELEMENT_NODE) return null
  const el = node as Element
  const kids = Array.from(el.childNodes).map((c, i) => render(c, `${key}.${i}`))
  if (el.tagName === 'BR') return <br key={key} />
  if (el.tagName === 'P' || el.tagName === 'DIV') {
    return (
      <p key={key} className="ofp-p">
        {kids.length ? kids : <br />}
      </p>
    )
  }
  if (el.tagName === 'A') {
    const href = linkHref(el.getAttribute('href'))
    return href ? (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="ofp-link">
        {kids}
      </a>
    ) : (
      <Fragment key={key}>{kids}</Fragment>
    )
  }
  const Tag = INLINE[el.tagName] as 'strong' | undefined
  return Tag ? <Tag key={key}>{kids}</Tag> : <Fragment key={key}>{kids}</Fragment>
}

export function OfHtml({ html, className = 'ofp-text' }: { html: string; className?: string }) {
  if (!html) return null
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  return <div className={className}>{Array.from(doc.body.childNodes).map((n, i) => render(n, String(i)))}</div>
}
